import {
  ArrowLeftOutlined,
  EyeOutlined,
  RiseOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  WalletOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { Alert, Button, Col, Progress, Row, Segmented, Skeleton, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";

import type { AdminRobotAnalytics, StatPeriod } from "@/entities/robot";
import { useRobot, useRobotAnalytics } from "@/entities/robot";
import { errorMessage } from "@/shared/api";
import { ROUTES, SECTIONS } from "@/shared/config";
import { formatDate, formatDateTime, formatMoney, formatPercent } from "@/shared/lib";
import { EmptyState, PageHeader, SectionCard, StatCard } from "@/shared/ui";

/**
 * Аналитика робота (доработки §7, строка «Robot analytics»).
 *
 * Отдельной страницей, а не вкладкой формы: форму открывают, чтобы что-то
 * изменить, и держать рядом с полями ввода агрегаты по всем покупкам значило
 * бы каждый раз считать их ради правки одной строки описания.
 *
 * Два блока с разной природой, и разделены они намеренно. Аудитория и деньги
 * — состояние на сейчас, периодом не ограничены: «AUM за 7 дней» не значит
 * ничего. Просмотры, запуски и отказы — события, и период к ним применяется.
 * Смешать их в один ряд плиток значило бы, что переключатель периода меняет
 * половину чисел и не меняет другую без всякого объяснения.
 */
type Row = AdminRobotAnalytics["series"][number];

const PERIODS: StatPeriod[] = ["7d", "30d", "90d"];

export function RobotAnalyticsPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const robotId = Number(id);
  const [period, setPeriod] = useState<StatPeriod>("30d");

  const robot = useRobot(robotId, Number.isFinite(robotId));
  const report = useRobotAnalytics(robotId, period, Number.isFinite(robotId));

  if (!Number.isFinite(robotId)) {
    return <Alert type="error" showIcon message={t("robots.notFound")} />;
  }
  if (report.isError) {
    return <Alert type="error" showIcon message={errorMessage(report.error)} />;
  }

  const columns: ColumnsType<Row> = [
    {
      title: t("robots.stats.day"),
      dataIndex: "date",
      key: "date",
      render: (value: string) => formatDate(value),
    },
    { title: t("robots.stats.col.views"), dataIndex: "views", key: "views" },
    { title: t("robots.stats.col.buys"), dataIndex: "buys", key: "buys" },
    { title: t("robots.stats.col.errors"), dataIndex: "buy_errors", key: "buy_errors" },
  ];

  const data = report.data;

  return (
    <>
      <PageHeader
        section="robots"
        title={robot.data?.name ?? t("robots.stats.title")}
        subtitle={t("robots.stats.subtitle")}
        before={
          <Link to={ROUTES.robot(robotId)}>
            <Button type="text" shape="circle" icon={<ArrowLeftOutlined aria-hidden />} />
          </Link>
        }
        extra={
          <Segmented<StatPeriod>
            value={period}
            onChange={setPeriod}
            options={PERIODS.map((value) => ({
              value,
              label: t(`robots.stats.period.${value}`),
            }))}
          />
        }
      />

      {report.isPending || data === undefined ? (
        <Skeleton active />
      ) : (
        <>
          {/* Аудитория и капитал — состояние на сейчас. Период их не трогает,
              и подпись блока обязана это говорить: иначе переключатель выше
              выглядит сломанным. */}
          <SectionCard color={SECTIONS.robots.color} title={t("robots.stats.audience")}>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} xl={6}>
                <StatCard
                  color={SECTIONS.robots.color}
                  icon={<TeamOutlined aria-hidden />}
                  title={t("robots.activeUsers")}
                  value={data.active_users}
                  hint={t("robots.stats.participants", { count: data.participants })}
                />
              </Col>
              <Col xs={24} sm={12} xl={6}>
                <StatCard
                  color={SECTIONS.robots.color}
                  icon={<WalletOutlined aria-hidden />}
                  title={t("robots.aumLabel")}
                  value={formatMoney(data.aum)}
                />
              </Col>
              <Col xs={24} sm={12} xl={6}>
                <StatCard
                  color={SECTIONS.robots.color}
                  icon={<RiseOutlined aria-hidden />}
                  title={t("robots.userProfitTotal")}
                  value={formatMoney(data.user_profit_total, { sign: "always" })}
                />
              </Col>
              <Col xs={24} sm={12} xl={6}>
                <StatCard
                  color={SECTIONS.robots.color}
                  icon={<ThunderboltOutlined aria-hidden />}
                  title={t("robots.purchasesCount")}
                  value={data.purchases_count}
                  hint={
                    data.avg_days === null || data.avg_days === undefined
                      ? t("common.noData")
                      : t("robots.stats.avgDays", { count: data.avg_days })
                  }
                />
              </Col>
            </Row>
          </SectionCard>

          <SectionCard
            color={SECTIONS.robots.color}
            title={t("robots.stats.funnel", { period: t(`robots.stats.period.${period}`) })}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} xl={6}>
                <StatCard
                  color={SECTIONS.robots.color}
                  icon={<EyeOutlined aria-hidden />}
                  title={t("robots.stats.views")}
                  value={data.views}
                />
              </Col>
              <Col xs={24} sm={12} xl={6}>
                <StatCard
                  color={SECTIONS.robots.color}
                  icon={<ThunderboltOutlined aria-hidden />}
                  title={t("robots.stats.buys")}
                  value={data.buys}
                />
              </Col>
              <Col xs={24} sm={12} xl={6}>
                {/* Прочерк вместо «0%» при нуле просмотров: делить не на что, а
                    «0%» читалось бы как «заходят и не покупают». */}
                <StatCard
                  color={SECTIONS.robots.color}
                  icon={<RiseOutlined aria-hidden />}
                  title={t("robots.stats.conversion")}
                  value={
                    data.conversion_pct === null || data.conversion_pct === undefined
                      ? t("common.noData")
                      : formatPercent(data.conversion_pct)
                  }
                  hint={t("robots.stats.conversionHint")}
                />
              </Col>
              <Col xs={24} sm={12} xl={6}>
                <StatCard
                  color={SECTIONS.robots.color}
                  icon={<WarningOutlined aria-hidden />}
                  title={t("robots.stats.errors")}
                  value={data.buy_errors}
                  hint={t("robots.stats.errorsHint")}
                  accent={data.buy_errors > 0}
                />
              </Col>
            </Row>
          </SectionCard>

          {/* Кампания есть не у всех: у обычного робота блока просто нет —
              шкала без знаменателя не значила бы ничего. */}
          {data.fill_pct !== null && data.fill_pct !== undefined && (
            <SectionCard color={SECTIONS.robots.color} title={t("robots.offer")}>
              <Progress percent={Number(data.fill_pct)} status="active" />
              {data.offer_state !== null && data.offer_state !== undefined && (
                <Tag>{t(`robots.offerStateValue.${data.offer_state}`)}</Tag>
              )}
            </SectionCard>
          )}

          <SectionCard
            color={SECTIONS.robots.color}
            bodyPadding={0}
            title={t("robots.stats.byDay")}
            extra={t("robots.stats.calculatedAt", {
              at: formatDateTime(data.calculated_at),
            })}
          >
            {data.series.length === 0 ? (
              <EmptyState title={t("robots.stats.empty")} />
            ) : (
              <Table<Row>
                rowKey="date"
                size="small"
                pagination={false}
                columns={columns}
                dataSource={data.series}
              />
            )}
          </SectionCard>
        </>
      )}
    </>
  );
}
